import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Http from "../../Http";
import { Loan_API } from "../../Helper";

import { motion } from "framer-motion";
import { FaCar, FaMoneyBillWave, FaShieldAlt, FaCheck,  FaCalendarAlt, FaUserCheck } from "react-icons/fa";
import { ArrowRight } from "lucide-react";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";


function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const CarLoanNew = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const faqs = [
    {
      question: " What is a car loan?",
      answer:
        "A car loan is a financial arrangement in which a lender provides funds to help you purchase a vehicle. You agree to repay the loan over a specified period, typically with interest, through regular installments. The car often serves as collateral for the loan, meaning the lender can repossess it if you default on payments.",
    },
    {
      question: "What factors should I consider when choosing a car loan?",
      answer:
        "When selecting a car loan, consider factors like the interest rate, loan term (duration), monthly payment amount, total loan cost, and any additional fees. You should also evaluate your credit score, as it can affect the terms and interest rates you qualify for. Look for prepayment penalties or other clauses that might impact your flexibility.",
    },
    {
      question: "Which bank offers the lowest interest rate on car loans?",
      answer:
        "The interest rate for car loans varies based on several factors, including the loan amount, your credit score (CIBIL score), loan tenure, and your financial history. To find the best rate, it is essential to compare offers from different banks and lending institutions. You can get personalized rates by providing your financial information to lenders and using our comparison tools to assess the available options.",
    },
    {
      question: "Can I get a car loan through TheFinMaster.com?",
      answer:
        "TheFinMaster.com does not provide car loans directly. However, we offer educational resources, loan comparison tools, and unbiased information to help you understand car loans and choose the best option for your needs. Once you have identified a suitable loan, you will be directed to the lenders website to complete a secure application process.",
    },
    {
      question: "What is the maximum tenure for a car loan?",
      answer:
        " Car loan tenures typically range from 1 to 7 years. A longer tenure results in lower EMIs but may increase the total interest paid over time. Choose a tenure that balances affordability and overall cost.",
    },
    {
      question: "Do I need a down payment to get a car loan?",
      answer:
        " Yes, most car loans require a down payment, usually 10%–25% of the vehicle’s cost. The higher the down payment, the lower your loan amount and interest burden. Some lenders also offer 100% on-road funding for select models.",
    },
    {
      question: "Can I get a car loan without a credit history?",
      answer:
        " While having a credit history helps, some banks offer car loans to first-time borrowers based on income proof and job stability. However, the interest rate may be slightly higher compared to someone with a strong credit profile.",
    },
    {
      question: "How is my car loan EMI calculated?",
      answer:
        " Your EMI depends on the loan amount, interest rate, and tenure. You can use a car loan EMI calculator to estimate your monthly outgo and plan your finances accordingly.",
    },
    {
      question: "What documents are required for a car loan application?",
      answer:
        " Typically, you need identity proof, address proof, income documents (like salary slips or ITRs), bank statements, and car-related documents such as the quotation or invoice from the dealer.",
    },
    {
      question: "Can I prepay my car loan before the tenure ends?",
      answer:
        " Yes, most banks allow partial or full prepayment of a car loan. However, some may charge a prepayment penalty, especially on fixed-rate loans. Always check the terms before making early repayments.",
    },
    {
      question: "Will I get tax benefits on a car loan?",
      answer:
        " Car loans do not offer tax benefits for personal use vehicles. However, if the car is purchased for business purposes, you may claim tax deductions on the interest and depreciation as per applicable rules.",
    },
    {
      question: "Is car loan interest fixed or floating?",
      answer:
        "Car loans generally come with fixed interest rates, ensuring consistent EMIs. Some lenders offer floating rate options too, but fixed rates are more common and preferred for predictable budgeting.",
    },
    {
      question: "Can I apply for a car loan online?",
      answer:
        " Yes, most banks and NBFCs offer online car loan applications. You can compare offers, upload documents, and get instant approvals digitally, making the process quick and convenient.",
    },
    {
      question: "What happens if I default on a car loan?",
      answer:
        " Missing EMIs can lead to penalty charges, damage to your credit score, and eventual repossession of the car. It’s essential to stay consistent with repayments or inform the lender in advance if facing financial issues.",
    },
    {
      question: "Does the car loan cover insurance and registration costs?",
      answer:
        " Some lenders offer loans that include insurance premiums and registration fees as part of the total loan amount. This is known as on-road price financing and helps reduce the initial out-of-pocket expense.",
    },
    {
      question: "Can I transfer my car loan to another bank?",
      answer:
        " Yes, through a process called balance transfer, you can shift your existing loan to another lender offering a lower interest rate. This helps in reducing your EMI burden and overall interest payout.",
    },
    {
      question: "How long does it take to get a car loan approved?",
      answer:
        "  Car loan approvals can be instant or take up to 2–3 working days, depending on your documentation, credit profile, and the lender’s process. Pre-approved customers usually get faster disbursals.",
    },
  ];

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };

  const components = [
    {
      id: 1,
      emoji: "💰",
      title: " Loan Principal (Borrowed Amount)",
      description:
        "The principal amount is the total sum borrowed from the lender to purchase a car. This does not include additional charges like interest, processing fees, or other levies. The loan principal depends on the car’s cost, loan-to-value (LTV) ratio, and borrower eligibility.",
    },
    {
      id: 2,
      emoji: "💵",
      title: "Down Payment",
      description:
        "A down payment is the amount paid upfront by the borrower at the time of purchase. Most banks finance up to 90-100% of the car’s on-road price, meaning borrowers must pay the remaining amount as a down payment. A higher down payment reduces the loan amount, lowering interest costs in the long run.",
    },
    {
      id: 3,
      emoji: "📉",
      title: "Rate of Interest",
      description:
        "The interest rate determines the cost of borrowing and is applied to the principal amount. Car loan interest rates in India typically range from 8.60% to 14% per annum, depending on the borrower’s credit score, income, and the lender’s terms. Fixed and floating interest rate options may be available.",
    },
    {
      id: 4,
      emoji: "⏳",
      title: "Loan Tenure",
      description:
        "The loan tenure is the period for which the borrower agrees to repay the loan in equated monthly installments (EMIs). It typically ranges from 1 to 7 years, with longer tenures resulting in lower monthly EMIs but higher overall interest payments. Borrowers should select a tenure that balances affordability and total interest cost",
    },
    {
      id: 5,
      emoji: "📆",
      title: "EMI (Equated Monthly Installment)",
      description:
        "EMI is the fixed monthly repayment amount, consisting of both principal and interest. It is calculated using the loan amount, interest rate, and tenure. A higher loan amount or interest rate increases EMI, while a longer tenure reduces it. Lenders often provide EMI calculators to help borrowers plan repayments.",
    },
  ];
  useEffect(() => {
    fetchLoanData();
  }, []);
  const fetchLoanData = async () => {
    try {
      const response = await Http.get(Loan_API);

      // The response structure might have nested data
      // Check if response has an insurance property (like in the original fetch response)
      const data = response.loan || response;

      // Filter the insurance data to show only Car category
      const carLoanData = Array.isArray(data)
        ? data.filter((insurance) => insurance.category_name === "Car Loan")
        : [];

      setLoanData(carLoanData);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch Loan data");
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  const eligibilityCriteria = [
    {
      id: 1,
      emoji: "🎂",
      title: "Age Criteria",
      description: `Applicants must be between 18 to 75 years at loan maturity. Self-employed individuals may have different age limits based on business stability.`,
    },
    {
      id: 2,
      emoji: "💼",
      title: "Employment Type",
      description: `Lenders offer car loans to both salaried and self-employed individuals.
      - Salaried: Must have a stable job in a government or private sector company.
      - Self-employed: Should have a stable income & at least two years of business continuity.`,
    },
    {
      id: 3,
      emoji: "💰",
      title: "Minimum Income Requirement",
      description: `Applicants must meet a minimum income requirement:
      - Salaried: ₹20,000 - ₹25,000 per month.
      - Self-employed: ₹2-3 lakh annually, verified through ITR filings.`,
    },
    {
      id: 4,
      emoji: "📆",
      title: "Employment Stability",
      description: `For salaried individuals, banks typically require at least one year of continuous employment, with a minimum of six months in the current job. Self-employed individuals must show at least two years of business existence.`,
    },
    {
      id: 5,
      emoji: "📊",
      title: "Credit Score & Financial History",
      description: `A credit score of **700+** is preferred for quick approval & lower interest rates. A strong repayment history, low outstanding debt, and responsible credit usage increase eligibility.`,
    },
  ];
  const carloanbrand = [
    {
      name: 'Maruti Suzuki ',
      path: '/maruti-suzuki-car-loan',
      image: '/assets/suzukicar.png',
    },
    {
      name: 'Hyundai ',
      path: '/hyundai-car-loan',
      image: '/assets/hondacar.png',
    },
    {
      name: 'Tata Motors ',
      path: '/tata-motors-car-loan',
      image: '/assets/tatacar.png',
    },
    {
      name: 'Mahindra ',
      path: '/mahindra-car-loan',
      image: '/assets/mahindracar.png',
    },
    {
      name: 'Kia ',
      path: '/kia-car-loan',
      image: '/assets/kiacar.png',
    },
   
    {
      name: 'Toyota ',
      path: '/toyota-car-loan',
      image: '/assets/toyotacar.png',
    },
    {
      name: 'Nissan ',
      path: '/nissan-car-loan',
      image: '/assets/nissan.png',
    },
    {
      name: 'Skoda',
      path: '/skoda-car-loan',
      image: '/assets/skoda.png',
    },
    {
      name: 'MG',
      path: '/mg-car-loan',
      image: '/assets/mgcar.png',
    },
    {
      name: 'Honda',
      path: '/honda-car-loan',
      image: '/assets/hondaicon.png',
    },
    {
      name: 'Renault',
      path: '/renault-car-loan',
      image: '/assets/renaultcar.png',
    },
    {
      name: 'Volkswagen',
      path: '/volkswagen-car-loan',
      image: '/assets/volkswagencar.jpg',
    },
   
  ];
  const carloanluxuarybrand = [
    {
      name: 'Mercedes',
      path: '/mercedes-car-loan',
      image: '/assets/mercedescar.png',
    },
    {
      name: 'BMW',
      path: '/bmw-car-loan',
      image: '/assets/bmwcar.png',
    },
    {
      name: 'Audi',
      path: '/audi-car-loan',
      image: '/assets/odicar.png',
    },
   
    {
      name: 'Volvo',
      path: '/volvo-car-loan',
      image: '/assets/volvocar.png',
    },
    {
      name: 'Lexus',
      path: '/lexus-car-loan',
      image: '/assets/lexuscar.jpg',
    },
    {
      name: 'Jaguar',
      path: '/jaguar-car-loan',
      image: '/assets/jaguarcar.png',
    },
    {
      name: 'Porsche',
      path: '/porsche-car-loan',
      image: '/assets/porschecar.png',
    },
  ];
const comparisonData = [
  { feature: "Purpose", carLoan: "Specifically for purchasing a car", personalLoan: "Can be used for any purpose, including buying a car", icon: <FaCar /> },
  { feature: "Interest Rates", carLoan: "8.5% to 14% (Lower)", personalLoan: "10% to 24% (Higher)", icon: <FaMoneyBillWave /> },
  { feature: "Security", carLoan: "Secured – The car itself is collateral", personalLoan: "Unsecured – No collateral required", icon: <FaShieldAlt /> },
  { feature: "Eligibility", carLoan: "Available even with a moderate credit score", personalLoan: "Requires a high credit score for better terms", icon: <FaUserCheck /> },
  { feature: "Loan Amount", carLoan: "Covers up to 90-100% of the car’s value", personalLoan: "Depends on credit score & income", icon: <FaMoneyBillWave /> },
  { feature: "Repayment Tenure", carLoan: "1 to 7 years", personalLoan: "1 to 5 years", icon: <FaCalendarAlt /> },
  { feature: "Ownership", carLoan: "Car remains hypothecated until full repayment", personalLoan: "No restrictions on ownership", icon: <FaCheck /> },
];
const points = [
  {
      title: ' Lower Interest Rates',
      description: `Since car loans are secured, they come with lower interest rates compared to personal loans. This makes them a cost-effective borrowing option.`
  },
  {
      title: 'Easier Approval',
      description: `Even if you have a moderate credit score, you can qualify for a car loan since the vehicle itself acts as collateral. This makes car loans more accessible than unsecured loans.`
  },
  {
      title: 'Higher Loan Amount',
      description: `Car loans can cover up to 100% of the car’s on-road price, reducing the need for a large upfront payment.`
  },
  {
      title: 'Structured Repayment Terms',
      description: `Lenders offer flexible repayment periods, usually ranging from 1 to 7 years, allowing borrowers to choose a tenure that suits their budget.`
  },

];
const points1 = [
  {
      title: ' Mandatory Down Payment',
      description: `Borrowers typically need to pay 5-20% of the car’s cost upfront, depending on the lender’s policies. This can be a financial burden for some buyers.`
  },
  {
      title: 'Vehicle Hypothecation',
      description: `The lender retains ownership of the car until the loan is fully repaid. Failure to repay the loan can result in repossession of the vehicle.`
  },
  {
      title: 'Limited Usage',
      description: `Unlike personal loans, a car loan can only be used for purchasing a vehicle and cannot be diverted for other financial needs.`
  },
 

];
const points2 = [
  {
      title: 'Opt for a Car Loan',
      description: ` If you want lower interest rates, longer repayment terms, and don’t mind your car being used as collateral.`
  },
  {
      title: 'Choose a Personal Loan',
      description: `If you want flexibility in usage, don’t want your car hypothecated, or need additional funds beyond just the vehicle cost.`
  },

];
const points3 = [
  {
      title: 'Compare Different Lenders',
      description: `Before finalizing a loan, compare multiple lenders, including banks, NBFCs, and online lending platforms. Look at their interest rates, loan tenure, processing fees, and customer service. A thorough comparison can help you find the best deal.`
  },
  {
      title: 'Interest Rate & Loan Amount',
      description: `Interest rates significantly impact the total repayment amount. Choose a loan that offers the most competitive interest rate along with a suitable loan amount. Some lenders provide up to 100% financing on the car’s on-road price, reducing the need for a large down payment.`
  },
  {
    title: ' Loan Tenure & EMI Affordability',
    description: ` Selecting the right loan tenure is important. A longer tenure (5-7 years) results in lower EMIs but increases the total interest paid. A shorter tenure means higher EMIs but helps you clear the debt faster. Use an EMI calculator to determine a repayment plan that fits your budget.`
},
{
    title: ' Hidden Charges & Processing Fees',
    description: `Car loans may come with hidden charges, such as processing fees, prepayment penalties, foreclosure charges, and documentation fees. Always read the terms and conditions carefully to avoid unexpected costs.`
},
{
  title: ' Special Offers & Discounts',
  description: ` Banks and lenders often provide seasonal discounts, festive offers, or lower interest rates on car loans. Look out for such promotions to avail of better loan terms and save mone.`
},
{
  title: 'Choose the Right Car & Budget Accordingly',
  description: `Before applying for a loan, decide on the car model and ensure that its cost aligns with your financial capability. Factor in fuel efficiency, maintenance costs, and insurance premiums, as these are recurring expenses that will impact your budget.`
},
{
  title: 'Check for Loan Insurance',
  description: `Some lenders offer car loan insurance that covers repayment in case of unforeseen circumstances like job loss or disability. While optional, it provides financial security and ensures that your family is not burdened with the repayment.`
},
]
const features = [
  { icon: '🚗', text: 'Accurate EMI & Interest Breakdown' },
  { icon: '⏱️', text: 'Takes Less Than a Minute' },
  { icon: '🧮', text: 'Auto-Adjusts Based on Tenures' },
  { icon: '📲', text: 'Clear View of Total Payable Amount' },
  { icon: '🔒', text: 'Works Seamlessly on All Devices' },
  { icon: '💰', text: 'Helps Plan Budget with Ease' },
];
  return (
    <>
      <Helmet>
        <title>
        Car Loans in India – Best Interest Rates & EMI Options
        </title>
        <meta
          name="description"
          content="Compare car loan offers from top banks. Get low interest rates, quick approval, and flexible EMIs. Apply online today."
        />
        <meta
          name="keywords"
          content="auto loan, car loan rates, pre approved car loan, auto financing, car financing"
        />
         <link rel="canonical" href="https://thefinmaster.com/car-loan" /> 
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      {/* Your existing content */}
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">Car Loan</span>
          </li>
          {/* <li className="flex items-center">
                 <Link to="/credit-card" className="text-blue-600 hover:text-blue-800">Credit Card</Link>
                 <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                   <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                 </svg>
               </li> */}
          {/* <li className="flex items-center">
                 <span className="text-gray-500">Car Loan EMI Calculator</span>
               </li> */}
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-0 pl-6">
                  <span className="text-blue-600 ">
                    Get fast approval for your dream car today
                  </span>{" "}
                  Easy process, low rates, drive away with your dream car
                </h1>
                {/* <a href="#insurance_plans">
                  <button className="py-4 px-8  border rounded-full bg-blue-600 text-white shadow-lg hover:shadow-xl text-lg mt-10 font-medium">
                    See Plans
                  </button>
                </a> */}
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="/assets/caloan.png"
                alt="car-loan"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="shadow-lg rounded-lg p-4 md:p-8 lg:p-12 mb-6 px-6 mx-4 md:mx-8 lg:mx-10 text-gray-600 flex justify-center items-center">
        <p className="text-lg md:text-base lg:text-lg text-center">
          Rev up your car buying journey with TheFinMaster.com! Our platform
          simplifies your search by bringing together offers from various
          lenders. Easily compare interest rates and loan terms to find the
          perfect fuel for your car financing needs. Don't get stuck in the slow
          lane of car loans – TheFinMaster.com puts you in the driver's seat to
          find the best deal and cruise towards your dream car.
        </p>
      </div>
      <div className="container mx-auto">
      <div className="flex flex-col lg:flex-row">
      <div className="w-full lg:w-3/4">
      <div id="loan_plans" className="lg:pr-6 md:px-4 px-2">
      {loading ? (
              <div className="text-center py-10">Loading Loan options...</div>
            ) : error ? (
              <div className="text-center py-10 text-red-600">{error}</div>
            ) : loanData.length === 0 ? (
              <div className="text-center py-10">
                No car Loan options available at the moment.
              </div>
            ) : (
          loanData.map((loan) => (
            <div
              key={loan.id}
              className="md:w-full max-w-[900px] py-2 border shadow-lg overflow-hidden hover:shadow-xl rounded-lg flex flex-wrap md:flex-nowrap gap-5 justify-between mx-auto mt-4"
            >
              <div className="flex flex-wrap md:flex-nowrap w-full justify-center items-center md:justify-normal">
                <Link to={`/car-loan/${loan.page_url}`}>
                  <div className="w-[95%] md:w-[220px] overflow-hidden py-2 md:ml-2">
                    <img
                      className="w-full h-full object-contain"
                      alt={loan.page_title || "Loan Provider"}
                      src={loan.image}
                    />
                  </div>
                </Link>
              </div>

              <div className="w-full md:pl-0 pl-0 mt-0 md:mt-10 text-center">
                <p className="text-gray-500 font-medium">Interest Rate</p>
                <h4 className="text-2xl py-0.5 font-bold">
                  {loan.interest_rate}%
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-0 mt-0 md:mt-10 text-center">
                <p className="text-gray-500 font-medium">Loan Amount </p>
                <h4 className="text-2xl py-0.5 font-bold">
                  ₹{loan.loan_amount}
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-0 mt-0 md:mt-10 text-center">
                <p className="text-gray-500 font-medium">Processing Fee</p>
                <h4 className="text-2xl py-0.5 font-bold">
                  ₹{loan.processing_fee}
                </h4>
              </div>

              <div className="flex flex-wrap justify-center md:justify-between items-center my-5 w-full gap-2 px-4">
                <a
                  href={loan.bank_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="bg-green-500 text-white font-semibold py-2 px-4 rounded hover:bg-green-600 transition duration-300">
                  Apply Now
                  </button>
                </a>

                <Link to={`/car-loan/${loan.page_url}`}>
                  <button className="text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-300 bg-blue-500">
                   See Details
                  </button>
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
</div>
<div className="w-full lg:w-1/4 px-2 md:px-4 mt-6 lg:mt-0">
          <div className="sticky top-20">
            {/* Ad 1 - Visible on all screen sizes */}
            <AdBanner displayOrder="2" />
            
            {/* Ad 2 - Hidden on small mobile, visible on larger screens */}
            {/* <a href="https://example.com/premium-discount" target="_blank" rel="noopener noreferrer" className="hidden sm:block mt-6">
              <img 
                src="/assets/niyoo.png" 
                alt="Premium Discount Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a> */}
            
            {/* Compact ad for mobile only */}
            {/* <a href="https://example.com/mobile-offer" target="_blank" rel="noopener noreferrer" className="block sm:hidden mt-4">
              <img 
                src="/assets/niyoo.png" 
                alt="Mobile Special Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a> */}
          </div>
        </div>
      </div>
    </div>
      {/* <div id="loan_plans" className="lg:px-48">
        {loading ? (
          <div className="text-center py-10">Loading insurance options...</div>
        ) : error ? (
          <div className="text-center py-10 text-red-600">{error}</div>
        ) : (
          loanData.map((loan) => (
            <div
              key={loan.id}
              className="md:w-full max-w-[900px] py-2 border shadow-lg overflow-hidden hover:shadow-xl rounded-lg flex flex-wrap md:flex-nowrap gap-5 justify-between mx-auto mt-4"
            >
              <div className="flex flex-wrap md:flex-nowrap w-full justify-center items-center md:justify-normal">
                <Link to={`/car_loan/${loan.page_url}`}>
                  <div className="w-[95%] md:w-[220px] overflow-hidden py-2 md:ml-2">
                    <img
                      className="w-full h-full object-contain"
                      alt={loan.page_title || "Loan Provider"}
                      src={loan.image}
                    />
                  </div>
                </Link>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-6">
                <p className="text-gray-500 font-medium">Interest Rate</p>
                <h4 className="text-2xl py-0.5 font-bold">
                  {loan.interest_rate}%
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-6">
                <p className="text-gray-500 font-medium">Loan Amount </p>
                <h4 className="text-2xl py-0.5 font-bold">
                  ₹{loan.loan_amount}
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-6">
                <p className="text-gray-500 font-medium">Processing Fee</p>
                <h4 className="text-2xl py-0.5 font-bold">
                  ₹{loan.processing_fee}
                </h4>
              </div>

              <div className="flex flex-wrap justify-center md:justify-between items-center my-5 w-full gap-2 px-4">
                <a
                  href={loan.bank_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="bg-green-500 text-white font-semibold py-2 px-4 rounded hover:bg-green-600 transition duration-300">
                    Get Quote!
                  </button>
                </a>

                <Link to={`/car_loan/${loan.page_url}`}>
                  <button className="text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-300 bg-blue-500">
                    See Details
                  </button>
                </Link>
              </div>
            </div>
          ))
        )}
      </div> */}

      <div className="flex justify-center items-center  p-4 mt-10">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center mt-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            What is a Car Loan?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            A car loan is a financial product that allows individuals to
            purchase a vehicle by borrowing money from a bank, financial
            institution, or lender. Instead of paying the full amount upfront,
            the borrower repays the loan in monthly installments (EMIs) over a
            fixed tenure, which includes both the principal amount and interest.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Car loans are secured loans, meaning the vehicle itself serves as
            collateral. If the borrower fails to repay the loan, the lender has
            the right to seize the car to recover the outstanding amount.
            Interest rates on car loans can be fixed or floating, depending on
            the lender's terms.
          </p>
        </div>
      </div>

         <div className="p-4 sm:p-8 bg-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
              Choose Your Car Loan
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {carloanbrand.map((calc, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.04 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Link
              to={calc.path}
              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
            >
              <img
                src={calc.image}
                alt={calc.name}
                className="w-16 h-16 object-contain mb-2"
              />
              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                {calc.name}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
      </div>

      <div className="p-4 sm:p-8 bg-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
              Choose Your Luxury Car Loan
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {carloanluxuarybrand.map((calc, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.04 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Link
              to={calc.path}
              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
            >
              <img
                src={calc.image}
                alt={calc.name}
                className="w-16 h-16 object-contain mb-2"
              />
              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                {calc.name}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-6"> 
  <div className="flex flex-col md:flex-row justify-between items-center gap-10">
    {/* Left - Text and CTA */}
    <div className="max-w-xl space-y-6 text-center md:text-left">
      <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
        Calculate Your Car Loan <br />
        in <span className="text-blue-600">Seconds</span>
      </h2>
      <p className="text-gray-700 text-lg">
        Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
      </p>
      <Link
        to="/financial-tools-calculators/car-loan-emi-calculator"
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
      >
        Start Calculating <ArrowRight className="w-5 h-5" />
      </Link>
    </div>

    {/* Right - Feature List with Emojis */}
    <div className="space-y-4 text-left">
      {features.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="text-xl">{item.icon}</span>
          <span className="text-gray-800 font-medium">{item.text}</span>
        </div>
      ))}
    </div>
  </div>
</section>
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800">
        
Keys stats 

        </h2>
        {/* <p className="text-gray-600 text-sm md:text-base mt-2">
          Choosing the right car insurance is crucial for financial security on
          the road. Here are the three main types of car insurance plans:
        </p> */}

        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
            Market Size and Growth
            </h3>
            <p className="text-gray-700 text-sm mt-1">
            The Indian car loan market was valued at approximately ₹1.98 lakh crore in 2024 and is projected to reach ₹3.33 lakh crore by 2029, growing at a Compound Annual Growth Rate (CAGR) of 9.13% during this period.

            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🛡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
            Interest Rates
            </h3>
            <p className="text-gray-700 text-sm mt-1">
            As of March 2025, car loan interest rates in India start from as low as 8.60% per annum, depending on various factors including the lender, loan tenure, and the borrower's credit profile.

            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🔒</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
            Used Car Financing Growth
            </h3>
            <p className="text-gray-700 text-sm mt-1">
            The Indian used car financing market is projected to register a CAGR of 11% during the forecast period of 2025-2030, indicating a significant rise in the financing of pre-owned vehicles.


            </p>
          </div>
        </div>
      </div>

      <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
      <div className="max-w-7xl mx-auto px-6 py-6 text-center ">
  <h2 className="text-2xl md:text-4xl font-bold text-gray-900">Types of Car Loans</h2>
  <p className="text-gray-700 text-base md:text-lg mt-3 max-w-3xl mx-auto">
    The different types of car loans are mentioned below:
  </p>

  <div className="mt-8 grid md:grid-cols-3 gap-8 text-left">
    {[
      {
        id: 1,
        title: "New Car Loan",
        description:
          "New car loans can be used to purchase a brand-new car straight out of the showroom. Depending on the bank, the interest rates will vary. Up to 100% of the on-road price of the car may be provided as a loan.",
      },
      {
        id: 2,
        title: "Used Car Loan",
        description:
          "Lenders offer used car loans up to 80-85% of the price of the car at attractive interest rates for a loan tenure up to 5 years. However, certain lenders will offer loans for cars that have been purchased within the last 5 years.",
      },
      {
        id: 3,
        title: "Loan against Car",
        description:
          "When one is in dire need of funds, he or she can pledge his or her old car as collateral in order to obtain sufficient funds to purchase a new car. This is known as Loan against Car. If you have a bad credit score, you can pledge your old car to the bank as collateral to obtain some much-needed funds.",
      },
    ].map((loan) => (
      <div
        key={loan.id}
        className="flex items-start space-x-4 p-6 rounded-lg border border-gray-300 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white"
      >
        <span className="text-orange-500 text-3xl font-bold">{loan.id}</span>
        <div>
          <h3 className="font-bold text-gray-900 text-lg">{loan.title}</h3>
          <p className="text-gray-700 text-sm md:text-base mt-2">{loan.description}</p>
        </div>
      </div>
    ))}
  </div>
</div>
<div className="max-w-7xl mx-auto px-6 py-6 text-center">
      <h2 className="text-2xl md:text-4xl font-bold text-gray-900">🚗 Key Components of a Car Loan</h2>
      <p className="text-gray-700 text-base md:text-lg mt-3 max-w-3xl mx-auto">
        Understanding these key elements helps in making better loan decisions.
      </p>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
        {components.map((item) => (
          <motion.div
            key={item.id}
            className="p-6 rounded-xl border border-gray-300 shadow-lg hover:shadow-xl transition-all duration-300 bg-white hover:-translate-y-1"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-start space-x-4">
              <span className="text-4xl">{item.emoji}</span>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{item.title}</h3>
                <p className="text-gray-700 text-sm md:text-base mt-2">{item.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 py-12">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
        📜 Eligibility Criteria for a Car Loan
      </h2>
      <p className="text-gray-700 text-base md:text-lg mt-3 text-center max-w-3xl mx-auto">
      Car loan eligibility varies across different lenders, but most financial institutions follow a common set of criteria. The eligibility is based on factors like age, income, employment status, and creditworthiness. Below are the standard requirements to qualify for a car loan in India:      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {eligibilityCriteria.map((item) => (
          <motion.div
            key={item.id}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="bg-white shadow-md border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center"
          >
            <div className="text-4xl">{item.emoji}</div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">
              {item.id}. {item.title}
            </h3>
            <p className="text-gray-600 text-md mt-2 whitespace-pre-line">{item.description}</p>
          </motion.div>
        ))}
      </div>
    </div>


    <div className="max-w-7xl mx-auto px-6 py-12">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
        🚗 Car Loan vs. Personal Loan: Which One Should You Choose?
      </h2>
      <p className="text-gray-700 text-base md:text-lg mt-3 text-center max-w-3xl mx-auto">
      When financing a car purchase, borrowers often choose between a car loan and a personal loan. While both options provide funds, they differ in terms of interest rates, repayment structures, eligibility criteria, and security requirements. Here’s a comparison to help you make an informed decision:

      </p>

      {/* Responsive Table */}
      <div className="hidden md:block mt-8 overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead className="bg-gray-200">
            <tr>
              <th className="p-3 text-left">Feature</th>
              <th className="p-3 text-left">🚗 Car Loan</th>
              <th className="p-3 text-left">💰 Personal Loan</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((row, index) => (
              <tr key={index} className="border-b border-gray-300 hover:bg-gray-100">
                <td className="p-3 flex items-center gap-2">{row.icon} {row.feature}</td>
                <td className="p-3">{row.carLoan}</td>
                <td className="p-3">{row.personalLoan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View: Stacked Cards */}
      <div className="mt-8 grid grid-cols-1 md:hidden gap-6">
        {comparisonData.map((row, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="bg-white shadow-md border border-gray-200 rounded-xl p-6 flex flex-col"
          >
            <div className="text-xl flex items-center gap-2 font-semibold text-gray-900">
              {row.icon} {row.feature}
            </div>
            <p className="mt-2 text-gray-600">
              <strong className="text-green-600">🚗 Car Loan:</strong> {row.carLoan}
            </p>
            <p className="mt-1 text-gray-600">
              <strong className="text-blue-600">💰 Personal Loan:</strong> {row.personalLoan}
            </p>
          </motion.div>
        ))}
      </div>
    </div>

    <div className=' px-2 md:px-6 bg-gray-100'>
        <div className=" p-6 ">
            <h2 className="text-3xl font-bold text-blue-700 mb-4">Pros and Cons of Car Loans</h2>
            <p className="text-gray-700 mb-6">
         ✅ Pros of Car Loans
             </p>

            {points.map((point, index) => (
                <div key={index} className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500">
                    <h2 className="text-xl font-semibold text-blue-600">{point.title}</h2>
                    <p className="text-gray-600">{point.description}</p>
                </div>
            ))}
        </div>
        </div>
        <div className=' px-2 md:px-6 bg-gray-100'>
        <div className=" p-6 ">
            <h2 className="text-3xl font-bold text-blue-700 mb-4">Pros and Cons of Car Loans</h2>
            <p className="text-gray-700 mb-6">
            ❌ Cons of Car Loans
             </p>

            {points1.map((point, index) => (
                <div key={index} className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500">
                    <h2 className="text-xl font-semibold text-blue-600">{point.title}</h2>
                    <p className="text-gray-600">{point.description}</p>
                </div>
            ))}
        </div>
        </div>
        <div className=' px-2 md:px-6 bg-gray-100'>
        <div className=" p-6 ">
            <h2 className="text-3xl font-bold text-blue-700 mb-4">Which Loan Should You Choose?</h2>
            {/* <p className="text-gray-700 mb-6">
            ❌ Cons of Car Loans
             </p> */}

            {points2.map((point, index) => (
                <div key={index} className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500">
                    <h2 className="text-xl font-semibold text-blue-600">{point.title}</h2>
                    <p className="text-gray-600">{point.description}</p>
                </div>
            ))}
        </div>
        </div>

        <div className=' px-2 md:px-6 bg-gray-100'>
        <div className=" p-6 ">
            <h3 className="text-3xl font-bold text-blue-700 mb-4">How to Choose the Right Car Loan: Key Factors to Consider</h3>
            <p className="text-gray-700 mb-6">
            Choosing the right car loan is crucial to ensure that your purchase remains affordable and does not strain your finances. Various lenders offer car loans with different terms, making it essential to compare and evaluate options carefully. Here are the key factors to check before selecting a car loan:
             </p>

            {points3.map((point, index) => (
                <div key={index} className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500">
                    <h2 className="text-xl font-semibold text-blue-600">{point.title}</h2>
                    <p className="text-gray-600">{point.description}</p>
                </div>
            ))}
        </div>
        </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6">
        <h2 className="text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CarLoanNew;
